/**
 * Client helpers for Jet AI chat (jurisdiction, advice, and casual conversation).
 */

import { api } from './api.js';
import { isJetLocalFaqIntent } from './jetReplies.js';
import {
  isJetConversationalFollowUp,
  isJetSmalltalk,
} from './jetSmalltalk.js';

const LEGAL_PATTERN =
  /collection costs?|attorney'?s? fees?|fee.?shift|recover(able)? (costs?|fees?)|add(ed)? to (the )?(total|amount|balance|owed)|inflat(e|ing) (the )?(demand|amount)|statutory (interest|fees?)|debtor lives in|lives in [a-z]|columbus|cleveland|cincinnati|ohio\b|fdcpa|consumer (vs|versus) commercial|can (i|we) (add|charge|recover|include)/i;

const OPEN_ADVICE_PATTERN =
  /how (do|should|can) (i|we)|what should i|help me|any advice|recommend|best way|am i (able|allowed)|is it (ok|okay|possible|legal)|should i|what happens if|can you (explain|help)/i;

/**
 * True when the question should use the Worker jet-chat playbook / OpenAI path
 * for recovery / jurisdiction topics.
 */
export function isJetLegalQuestion(message) {
  const q = String(message || '').trim();
  if (!q) return false;
  return LEGAL_PATTERN.test(q);
}

/**
 * Open how-to / advice questions that feel awkward as canned FAQ.
 */
export function isJetOpenAdviceQuestion(message, role = 'CLIENT', context = {}) {
  const raw = String(message || '').trim();
  if (!raw || isJetLegalQuestion(raw) || isJetSmalltalk(raw)) return false;
  // Explicit advice / how-to beats loose FAQ keyword matches (e.g. “the debtor”).
  if (OPEN_ADVICE_PATTERN.test(raw)) return true;
  if (isJetLocalFaqIntent(raw, role, context)) return false;
  // Longer explicit questions that aren't FAQ → AI teammate voice
  return raw.length >= 36 && /\?/.test(raw);
}

/**
 * Casual conversation that should stay chatty (optionally via AI with history).
 */
export function isJetChatConversation(message, context = {}) {
  if (isJetSmalltalk(message)) return true;
  return isJetConversationalFollowUp(message, {
    hadRecentSmalltalk: Boolean(context.hadRecentSmalltalk),
  });
}

export function shouldUseJetAiChat(message, role = 'CLIENT', context = {}) {
  // Small talk stays local/instant unless we explicitly want richer AI chat follow-ups.
  if (isJetSmalltalk(message)) return false;
  if (isJetConversationalFollowUp(message, context)) return true;
  // After an extraction, send any question to AI chat so it can answer from memory.
  if (context.hasExtraction && /\?/.test(String(message || ''))) return true;
  return (
    isJetLegalQuestion(message) || isJetOpenAdviceQuestion(message, role, context)
  );
}

export function jetAiChatMode(message, context = {}) {
  if (isJetLegalQuestion(message)) return 'legal';
  if (isJetChatConversation(message, context)) return 'chat';
  return 'general';
}

/**
 * Call Worker jet-chat. Falls back to a local template if offline.
 */
export async function askJetLegalChat(opts = {}) {
  return askJetAiChat(opts);
}

/**
 * @param {{
 *   message: string,
 *   role?: string,
 *   debtorState?: string,
 *   mode?: string,
 *   priorTopic?: string,
 *   history?: Array<{ role: string, text: string }>,
 *   hadRecentSmalltalk?: boolean,
 * }} opts
 */
export async function askJetAiChat({
  message,
  role = 'CLIENT',
  debtorState = '',
  mode,
  priorTopic = '',
  history = [],
  hadRecentSmalltalk = false,
} = {}) {
  const text = String(message || '').trim();
  if (!text) {
    const err = new Error('Ask Jet a question first.');
    err.code = 'EMPTY';
    throw err;
  }

  const resolvedMode = mode || jetAiChatMode(text, { hadRecentSmalltalk });

  try {
    const data = await api.post('/server/client_api/ai/jet-chat', {
      message: text,
      role,
      debtorState: debtorState || undefined,
      mode: resolvedMode,
      priorTopic: priorTopic || undefined,
      history: Array.isArray(history) ? history.slice(-8) : [],
    });
    return {
      text: String(data?.text || '').trim(),
      links: Array.isArray(data?.links) ? data.links : [],
      source: data?.source || 'api',
      jurisdiction: data?.jurisdiction || null,
      usedOpenAI: Boolean(data?.usedOpenAI ?? data?.source === 'openai'),
      model: data?.model || null,
      fallbackReason: data?.fallbackReason || null,
      mode: resolvedMode,
    };
  } catch {
    return localAiFallback(text, role, resolvedMode);
  }
}

function localAiFallback(message, role, mode) {
  const q = String(message || '').toLowerCase();
  const ohio = /ohio|columbus|cleveland|cincinnati/.test(q);
  const links =
    String(role || '').toUpperCase() === 'ATTORNEY'
      ? [{ label: 'Open queue', to: '/attorney' }]
      : [
          { label: 'Messages', to: '/client/messages' },
          { label: 'Guides', to: '/client/guides' },
        ];

  if (mode === 'chat') {
    return {
      text:
        'Doing well on my end — thanks for asking. I’m here if you want to talk demands, evidence, or just say hi.',
      links: [],
      source: 'local-fallback',
      jurisdiction: null,
      usedOpenAI: false,
      model: null,
      fallbackReason: 'Jet chat API unavailable',
      mode: 'chat',
    };
  }

  if (mode === 'legal' || ohio) {
    if (ohio) {
      return {
        text:
          'Quick take: in Ohio (including Columbus), adding collection costs to the balance usually depends on the contract and whether it’s commercial or consumer debt. DemandJet’s platform fee is billed to you — not auto-added to what the debtor owes. Not legal advice; confirm with a licensed attorney.',
        links,
        source: 'local-fallback',
        jurisdiction: 'OH',
        usedOpenAI: false,
        model: null,
        fallbackReason: 'Jet chat API unavailable',
        mode: 'legal',
      };
    }
    return {
      text:
        'Short version: whether collection costs can be added depends on the contract, debt type, and jurisdiction. Our platform fee stays on your side of the ledger unless an attorney says something is recoverable. Not legal advice — ask counsel for the debtor’s state.',
      links,
      source: 'local-fallback',
      jurisdiction: null,
      usedOpenAI: false,
      model: null,
      fallbackReason: 'Jet chat API unavailable',
      mode: 'legal',
    };
  }

  return {
    text:
      'Got it — I’m offline from the AI route right now. Try asking about pricing, sending a demand, or evidence uploads, or ping your attorney from Messages if it’s case-specific.',
    links,
    source: 'local-fallback',
    jurisdiction: null,
    usedOpenAI: false,
    model: null,
    fallbackReason: 'Jet chat API unavailable',
    mode: 'general',
  };
}
